import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Gift, Copy, Check, Share2, Twitter, Linkedin, Mail, Loader2, Users, Coins, ArrowRight, LogIn,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, accent = 'bg-neutral-50 border-neutral-200' }) {
  return (
    <div className={`rounded-2xl border ${accent} p-5`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-1.5 font-display text-3xl font-bold text-neutral-900">{value}</div>
    </div>
  );
}

export default function ReferralsPage() {
  const { user, login, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/referrals/me`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const code = data?.referral_code || user?.referral_code || '';
  const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/?ref=${code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Select the link manually.');
    }
  };

  const shareUrls = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      "I'm shipping full-stack apps in minutes with Transformerus. Use my link, you get $200 in credits, I do too:"
    )}&url=${encodeURIComponent(link)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
    email: `mailto:?subject=${encodeURIComponent('Build your next app on Transformerus')}&body=${encodeURIComponent(
      'I have been using Transformerus to ship full-stack apps in minutes. Sign up with my link and we both get $200 in credits: ' + link
    )}`,
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return '—'; }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-1 pt-28 pb-24">
          <div className="mx-auto max-w-xl px-6 text-center">
            <div className="inline-flex w-12 h-12 rounded-2xl bg-emerald-100 items-center justify-center">
              <Gift className="w-6 h-6 text-emerald-600" />
            </div>
            <h1 className="mt-5 font-display text-3xl font-bold text-neutral-900 tracking-tight">
              Sign in to see your referrals
            </h1>
            <p className="mt-2 text-neutral-600">
              You need an account to track invites and claim your $200 referral rewards.
            </p>
            <Button onClick={login} className="mt-6 h-12 px-6 rounded-2xl bg-neutral-900 text-white">
              <LogIn className="w-4 h-4 mr-2" /> Sign in
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 pt-28 pb-24">
        <div className="mx-auto max-w-5xl px-6">
          <span className="inline-flex px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
            Refer & earn
          </span>
          <h1 className="mt-4 font-display text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight">
            Your referrals
          </h1>
          <p className="mt-3 text-lg text-neutral-600 max-w-2xl">
            Invite a builder. When they make their first paid purchase, you both get $200 in build credits — instantly added to both accounts.
          </p>

          {/* Stats */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <StatCard icon={Users} label="Total invites" value={data?.stats?.total ?? 0} />
            <StatCard icon={Loader2} label="Pending" value={data?.stats?.pending ?? 0} accent="bg-amber-50 border-amber-200" />
            <StatCard icon={Coins} label="Rewarded" value={data?.stats?.rewarded ?? 0} accent="bg-emerald-50 border-emerald-200" />
          </div>

          {/* Share */}
          <div className="mt-10 p-6 lg:p-8 bg-white border border-neutral-200 rounded-3xl shadow-sm">
            <h2 className="font-display text-xl font-semibold text-neutral-900">Share your link</h2>
            <p className="mt-1 text-sm text-neutral-600">Anyone who signs up via your link is attributed to you automatically.</p>
            <div className="mt-4 flex gap-2">
              <Input value={link} readOnly className="flex-1 text-sm bg-neutral-50 border-neutral-200" />
              <Button onClick={copy} className="h-10 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white px-4">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="mt-2 text-xs text-neutral-500">Code: <span className="font-mono text-neutral-800">{code}</span></p>

            <div className="mt-5 grid grid-cols-3 gap-2 max-w-md">
              <a href={shareUrls.twitter} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 h-10 rounded-xl bg-neutral-50 border border-neutral-200 hover:bg-neutral-100 text-sm text-neutral-700">
                <Twitter className="w-4 h-4" /> Twitter
              </a>
              <a href={shareUrls.linkedin} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 h-10 rounded-xl bg-neutral-50 border border-neutral-200 hover:bg-neutral-100 text-sm text-neutral-700">
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
              <a href={shareUrls.email}
                className="flex items-center justify-center gap-2 h-10 rounded-xl bg-neutral-50 border border-neutral-200 hover:bg-neutral-100 text-sm text-neutral-700">
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>
          </div>

          {/* History */}
          <div className="mt-8 bg-white border border-neutral-200 rounded-3xl overflow-hidden">
            <div className="px-6 py-5 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-neutral-900">Activity</h2>
              <Link to="/build" className="text-sm text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1">
                Back to builder <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            {loading ? (
              <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" /></div>
            ) : !data?.referrals?.length ? (
              <div className="p-10 text-center">
                <div className="inline-flex w-10 h-10 rounded-full bg-neutral-100 items-center justify-center mb-3">
                  <Share2 className="w-5 h-5 text-neutral-500" />
                </div>
                <h3 className="font-display font-semibold text-neutral-900">No invites yet</h3>
                <p className="mt-1 text-sm text-neutral-600">Share your link to start earning credits.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-500">
                  <tr>
                    <th className="text-left font-medium px-6 py-3">Referee</th>
                    <th className="text-left font-medium px-6 py-3 hidden sm:table-cell">Invited on</th>
                    <th className="text-left font-medium px-6 py-3">Status</th>
                    <th className="text-left font-medium px-6 py-3 hidden md:table-cell">Rewarded on</th>
                  </tr>
                </thead>
                <tbody>
                  {data.referrals.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-neutral-100">
                      <td className="px-6 py-4 font-mono text-xs text-neutral-700 truncate max-w-[200px]">{r.referee_user_id?.slice(-12) || '—'}</td>
                      <td className="px-6 py-4 text-neutral-600 hidden sm:table-cell">{formatDate(r.created_at)}</td>
                      <td className="px-6 py-4">
                        {r.status === 'rewarded' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
                            <Check className="w-3 h-3" /> +200 credits
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200">
                            Pending first purchase
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-neutral-600 hidden md:table-cell">{formatDate(r.rewarded_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
